import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Box, Typography, Table, TableHead, TableRow, TableCell, TableBody, Button, Stack, Chip,
    Dialog, DialogTitle, DialogContent, DialogActions, TextField, Tooltip,
    IconButton, Avatar, CircularProgress, FormControlLabel, RadioGroup, Radio,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/DeleteOutlineOutlined";
import EditIcon from "@mui/icons-material/EditOutlined";
import ContentCopyIcon from "@mui/icons-material/ContentCopyOutlined";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { toast } from "react-toastify";

import api, { apiError, tokens as session } from "../api";
import AdminHeader, { adminPage } from "../components/AdminHeader";

/*
 * Discount codes.
 *
 * Sales hands these out; this is where they come from. A code cuts a percentage off the
 * plan price before tax, on whichever product it is marked for, and it keeps applying
 * for as long as that subscription runs - so the figures here are not a one-off
 * giveaway, they are a price somebody will be paying next year too.
 *
 * Used codes are switched off rather than deleted: a customer paying less than the plan
 * price needs a reason that still exists when somebody asks in March.
 */

const th = {
    color: "rgba(255,255,255,.72)", fontSize: 12, fontWeight: 700,
    textTransform: "uppercase", letterSpacing: .6, whiteSpace: "nowrap",
    bgcolor: "rgba(255,255,255,.06)",
    borderBottom: "1px solid rgba(255,255,255,.18)",
};
const td = { color: "#fff", borderColor: "rgba(255,255,255,.07)" };

const darkField = {
    "& .MuiOutlinedInput-root": {
        color: "#fff", borderRadius: 2,
        bgcolor: "rgba(255,255,255,.04)",
        "& fieldset": { borderColor: "rgba(255,255,255,.16)" },
        "&:hover fieldset": { borderColor: "rgba(255,255,255,.32)" },
        "&.Mui-focused fieldset": { borderColor: "#7BD95F" },
    },
    "& .MuiInputLabel-root": { color: "rgba(255,255,255,.5)" },
    "& .MuiInputLabel-root.Mui-focused": { color: "#7BD95F" },
    "& .MuiSelect-icon": { color: "rgba(255,255,255,.55)" },

    /* Disabled, but still meant to be read: MUI greys it out with
       -webkit-text-fill-color, which on this paper is all but invisible. */
    "& .MuiOutlinedInput-root.Mui-disabled": {
        bgcolor: "rgba(255,255,255,.02)",
        "& fieldset": { borderColor: "rgba(255,255,255,.08)" },
        "& .MuiOutlinedInput-input": { WebkitTextFillColor: "rgba(255,255,255,.65)" },
    },
    "& .MuiInputLabel-root.Mui-disabled": { color: "rgba(255,255,255,.38)" },
};

/*
 * The calendar panel, coloured by hand.
 *
 * MUI 9 reads its colours from CSS variables, so handing the picker a nested theme with
 * `mode: "dark"` changes nothing - the components go on reading the light scheme's
 * variables and the dates come out near-black on a dark panel. Switching the whole app
 * to dark would restyle every screen built against the light one.
 *
 * So the surface is set here and everything inside inherits it, with only the two states
 * that must look different - unusable and chosen - named afterwards. Blunt, but it does
 * not depend on the internal class names of a library that has already renamed them once.
 */
/*
 * The picker's own text field.
 *
 * It is not an ordinary TextField underneath: v9 draws it with MuiPickersInputBase and
 * MuiPickersOutlinedInput, so the dark styles every other field here uses miss it
 * entirely and it comes out black text with no visible outline. Same look, named for
 * the classes the picker actually renders.
 */
const pickerField = {
    "& .MuiPickersOutlinedInput-root": {
        color: "#fff", borderRadius: 2,
        bgcolor: "rgba(255,255,255,.04)",
    },
    "& .MuiPickersSectionList-root": { color: "#fff" },
    // Through the root, because MUI's own rule for the outline is written that way and
    // a shorter selector loses to it - leaving a black border on a dark field.
    "& .MuiPickersOutlinedInput-root .MuiPickersOutlinedInput-notchedOutline": {
        borderColor: "rgba(255,255,255,.16)",
    },
    "& .MuiPickersOutlinedInput-root:hover .MuiPickersOutlinedInput-notchedOutline": {
        borderColor: "rgba(255,255,255,.32)",
    },
    "& .MuiPickersOutlinedInput-root.Mui-focused .MuiPickersOutlinedInput-notchedOutline": {
        borderColor: "#7BD95F",
    },
    "& .MuiInputLabel-root": { color: "rgba(255,255,255,.5)" },
    "& .MuiInputLabel-root.Mui-focused": { color: "#7BD95F" },
    "& .MuiIconButton-root": { color: "rgba(255,255,255,.55)" },
};

const pickerPaper = {
    backgroundColor: "#0C1A12",
    backgroundImage: "none",       // MUI lays an elevation tint over dark surfaces
    color: "#fff",
    border: "1px solid rgba(255,255,255,.14)",
    borderRadius: 3,

    /*
     * The day buttons are named one by one, not swept up by a `& *` rule.
     *
     * MUI gives them an explicit `rgba(0,0,0,.87)` from the light palette, which beats
     * anything inherited - so each state has to be answered directly. The class is
     * `MuiPickerDay-root` in v9; it was `MuiPickersDay-root` before, and styling the old
     * name silently does nothing, which is how this looked black on black for an hour.
     */
    "& .MuiPickerDay-root": { color: "#fff" },
    "& .MuiPickerDay-root:hover": { backgroundColor: "rgba(255,255,255,.08)" },
    "& .MuiPickerDay-root.Mui-disabled": { color: "rgba(255,255,255,.28)" },
    "& .MuiPickerDay-root.Mui-selected": { backgroundColor: "#2E7D20", color: "#fff" },
    "& .MuiPickerDay-today": { borderColor: "rgba(255,255,255,.4)" },

    // Everything else - header, arrows, weekday letters, the year list.
    "& .MuiTypography-root, & .MuiIconButton-root, & .MuiSvgIcon-root": { color: "#fff" },
    "& .MuiDayCalendar-weekDayLabel": { color: "rgba(255,255,255,.55)" },
    "& .MuiPickersYear-yearButton": { color: "#fff" },
    "& .MuiPickersYear-yearButton.Mui-selected": { backgroundColor: "#2E7D20" },
};

const dialogPaper = {
    sx: {
        bgcolor: "#0C1A12", color: "#fff", borderRadius: 3,
        border: "1px solid rgba(255,255,255,.12)", backgroundImage: "none",
    },
};

/* Round action buttons, as on the licences table. Disabled keeps the circle, loses the
   colour, so a row does not change shape when a coupon can no longer be deleted. */
const actionAvatar = (bg, off = false) => ({
    width: 28, height: 28,
    bgcolor: off ? "rgba(255,255,255,.06)" : bg,
    color: off ? "rgba(255,255,255,.25)" : "#fff",
});

/**
 * The fields of a coupon, for whichever dialog is asking.
 *
 * Written once and used by both. Two copies of the same form drift apart the first time
 * one of them is changed, and the person using them has to work out whether the
 * difference means anything.
 */
function CouponFields({ v, onChange, codeDisabled = false }) {
    const set = (k) => (e) => onChange(k, e.target.value);

    return (
        <Stack spacing={2.5} sx={{ mt: 1 }}>
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2.5 }}>
                <TextField size="small" label="Code" sx={darkField} disabled={codeDisabled}
                    value={v.code || ""}
                    onChange={(e) => onChange("code", e.target.value.toUpperCase())} />
                <TextField size="small" type="number" label="Discount (%)" sx={darkField}
                    value={v.percent || ""} onChange={set("percent")} />
            </Box>

            {/* Both optional, and both meaning "no limit" when left empty - said on the
                labels so nobody fills them in to be safe. */}
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2.5 }}>
                {/*
                  * MUI's own picker rather than the browser's.
                  *
                  * A native date input opens a white calendar drawn by Chrome, which
                  * cannot be styled and lands like a torn page in the middle of a dark
                  * dialog. This one is an ordinary MUI component, so it takes the same
                  * colours as everything else.
                  *
                  * Today and earlier are disabled: a coupon that expired before it was
                  * created is only ever a typing mistake.
                  */}
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker label="Expires (optional)" format="DD MMM YYYY"
                        value={v.expiresAt || null} minDate={dayjs().add(1, "day")}
                        onChange={(d) => onChange("expiresAt", d)}
                        slotProps={{
                            textField: { size: "small", fullWidth: true, sx: pickerField },
                            desktopPaper: { sx: pickerPaper },
                            mobilePaper: { sx: pickerPaper },
                            field: { clearable: true },
                            /*
                             * Above the dialog, not behind it. A popper and a dialog sit
                             * at the same z-index by default, so the dialog - later in
                             * the tree - paints over the calendar, and what looks like a
                             * transparent panel is really the form drawn on top of it.
                             */
                            popper: { sx: { zIndex: 1400 } },
                        }} />
                </LocalizationProvider>
                <TextField size="small" type="number" label="Max uses (optional)" sx={darkField}
                    value={v.maxUses || ""} onChange={set("maxUses")} />
            </Box>

            {/* Two named states rather than a switch: "Deactive" said out loud is
                clearer than an unlit toggle, which reads as "not set yet" as often as
                it reads as "off". */}
            <Box>
                <RadioGroup row value={v.active ? "active" : "deactive"}
                    onChange={(e) => onChange("active", e.target.value === "active")}>
                    {[["active", "Active"], ["deactive", "Deactive"]].map(([value, label]) => (
                        <FormControlLabel key={value} value={value} label={label}
                            sx={{ color: "#fff", mr: 3 }}
                            control={<Radio size="small" sx={{
                                color: "rgba(255,255,255,.4)",
                                "&.Mui-checked": { color: "#7BD95F" },
                            }} />} />
                    ))}
                </RadioGroup>
            </Box>
        </Stack>
    );
}

const BLANK = { code: "", percent: "", expiresAt: null, maxUses: "", active: true };

const fmtDate = (d) => (d
    ? new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })
    : "—");

/*
 * Why a code is not usable, or nothing when it is. The row says this rather than just
 * "inactive", because switched off, expired and fully used need different answers when
 * a customer rings up saying their code was refused.
 */
const stateOf = (c) => {
    if (!c.active) return { label: "Off", colour: "rgba(255,255,255,.45)", bg: "rgba(255,255,255,.07)" };
    if (c.expiresAt && new Date(c.expiresAt) < new Date()) {
        return { label: "Expired", colour: "#FF9B9B", bg: "rgba(229,62,62,.18)" };
    }
    if (c.maxUses && c.usedCount >= c.maxUses) {
        return { label: "Used up", colour: "#F3C969", bg: "rgba(214,158,46,.2)" };
    }
    return { label: "Active", colour: "#9FE08C", bg: "rgba(46,125,32,.25)" };
};

export default function AdminCoupons() {
    const navigate = useNavigate();
    const [rows, setRows] = useState(null);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState(BLANK);
    const [editing, setEditing] = useState(null);   // the coupon being switched on or off
    const [doomed, setDoomed] = useState(null);
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        try {
            const { data } = await api.get("/console/coupons");
            setRows(data.data || []);
        } catch (e) {
            if (e?.response?.status === 401) { session.console.clear(); navigate("/admin"); return; }
            toast.error(apiError(e));
            setRows([]);
        }
    }, [navigate]);

    useEffect(() => { load(); }, [load]);

    const create = async () => {
        setBusy(true);
        try {
            await api.post("/console/coupons", {
                ...form,
                active: form.active,
                // The picker hands back a dayjs object; the server wants a date it can read.
                expiresAt: form.expiresAt ? form.expiresAt.toISOString() : "",
            });
            toast.success(`${form.code.toUpperCase()} created`);
            setOpen(false);
            setForm(BLANK);
            load();
        } catch (e) { toast.error(apiError(e)); }
        setBusy(false);
    };

    /*
     * Opened with what the coupon already has, as strings and a dayjs date - the same
     * shapes the create form uses, so the two dialogs can share their fields.
     */
    const openEdit = (c) => setEditing({
        row: c,
        code: c.code,
        percent: String(c.percent || ""),
        expiresAt: c.expiresAt ? dayjs(c.expiresAt) : null,
        maxUses: c.maxUses ? String(c.maxUses) : "",
        active: c.active,
    });

    const saveEdit = async () => {
        setBusy(true);
        try {
            await api.patch(`/console/coupons/${editing.row._id}`, {
                code: editing.code,
                percent: Number(editing.percent) || 0,
                expiresAt: editing.expiresAt ? editing.expiresAt.toISOString() : "",
                maxUses: Number(editing.maxUses) || 0,
                active: editing.active,
            });
            toast.success(`${editing.row.code} updated`);
            setEditing(null);
            load();
        } catch (e) { toast.error(apiError(e)); }
        setBusy(false);
    };

    const remove = async () => {
        setBusy(true);
        try {
            await api.delete(`/console/coupons/${doomed._id}`);
            toast.success("Coupon deleted");
            setDoomed(null);
            load();
        } catch (e) { toast.error(apiError(e)); }
        setBusy(false);
    };

    const copy = (text) => {
        navigator.clipboard.writeText(text);
        toast.success("Copied");
    };

    const canCreate = Boolean(form.code.trim() && Number(form.percent) >= 1 && Number(form.percent) <= 100);

    return (
        <Box sx={{ ...adminPage, display: "flex", flexDirection: "column" }}>
            <AdminHeader />

            <Box sx={{ px: { xs: 2, md: 4 }, py: { xs: 3, md: 4 } }}>
                <Stack direction="row" spacing={2}
                    sx={{ alignItems: "center", justifyContent: "space-between", mb: 2.5 }}>
                    <Typography sx={{ fontWeight: 800, fontSize: 24, letterSpacing: -0.5 }}>
                        Coupons
                    </Typography>
                    <Button startIcon={<AddIcon />} onClick={() => setOpen(true)}
                        sx={{
                            borderRadius: 999, px: 2.5, color: "#9FE08C",
                            border: "1px solid rgba(123,217,95,.45)",
                            "&:hover": { bgcolor: "rgba(123,217,95,.1)" },
                        }}>
                        Generate Coupon
                    </Button>
                </Stack>

                <Box sx={{
                    borderRadius: 1.5, overflow: "hidden", overflowX: "auto",
                    bgcolor: "rgba(255,255,255,.04)", border: "1px solid rgba(255,255,255,.12)",
                }}>
                    {rows === null ? (
                        <Stack sx={{ alignItems: "center", py: 6 }}><CircularProgress size={24} /></Stack>
                    ) : !rows.length ? (
                        <Stack sx={{ alignItems: "center", py: 6, px: 3 }}>
                            <Typography sx={{ color: "rgba(255,255,255,.55)", fontSize: 14.5 }}>
                                No coupons yet. One cuts a percentage off the plan price, before tax,
                                for as long as that subscription runs.
                            </Typography>
                        </Stack>
                    ) : (
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={th} align="center">Actions</TableCell>
                                    <TableCell sx={th} align="center">Code</TableCell>
                                    <TableCell sx={th} align="center">Discount</TableCell>
                                    <TableCell sx={th} align="center">Expires</TableCell>
                                    <TableCell sx={th} align="center">Used</TableCell>
                                    <TableCell sx={th} align="center">Status</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {rows.map((c) => {
                                    const s = stateOf(c);
                                    return (
                                        <TableRow key={c._id} hover>
                                            {/* Both acts in one place, and in the order
                                                they are reached for: switching a coupon
                                                off is routine, deleting one is not. */}
                                            <TableCell sx={td} align="center">
                                                <Stack direction="row" spacing={.5} sx={{ justifyContent: "center" }}>
                                                    <Tooltip title="Edit coupon">
                                                        <IconButton sx={{ p: .3 }} onClick={() => openEdit(c)}>
                                                            <Avatar sx={actionAvatar("#2E7D20")}>
                                                                <EditIcon sx={{ fontSize: 16 }} />
                                                            </Avatar>
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title={c.usedCount
                                                        ? "Used - switch it off instead"
                                                        : "Delete this coupon"}>
                                                        <span>
                                                            <IconButton sx={{ p: .3 }} disabled={c.usedCount > 0}
                                                                onClick={() => setDoomed(c)}>
                                                                <Avatar sx={actionAvatar("#8C2F2F", c.usedCount > 0)}>
                                                                    <DeleteIcon sx={{ fontSize: 16 }} />
                                                                </Avatar>
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>
                                                </Stack>
                                            </TableCell>
                                            <TableCell sx={td} align="center">
                                                <Stack direction="row" spacing={.5}
                                                    sx={{ alignItems: "center", justifyContent: "center" }}>
                                                    <Typography sx={{ fontFamily: "monospace", fontSize: 14, letterSpacing: 1 }}>
                                                        {c.code}
                                                    </Typography>
                                                    <Tooltip title="Copy code">
                                                        <IconButton size="small" onClick={() => copy(c.code)}
                                                            sx={{ color: "rgba(255,255,255,.5)" }}>
                                                            <ContentCopyIcon sx={{ fontSize: 15 }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Stack>
                                            </TableCell>
                                            <TableCell sx={{ ...td, fontWeight: 700 }} align="center">
                                                {c.percent}%
                                            </TableCell>
                                            <TableCell sx={{ ...td, color: "rgba(255,255,255,.75)" }} align="center">
                                                {fmtDate(c.expiresAt)}
                                            </TableCell>
                                            <TableCell sx={{ ...td, color: "rgba(255,255,255,.75)" }} align="center">
                                                {c.usedCount}{c.maxUses ? ` / ${c.maxUses}` : ""}
                                            </TableCell>
                                            <TableCell sx={td} align="center">
                                                <Chip size="small" label={s.label}
                                                    sx={{
                                                        bgcolor: s.bg, color: s.colour, fontWeight: 600,
                                                        border: "1px solid rgba(255,255,255,.14)",
                                                    }} />
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    )}
                </Box>
            </Box>

            {/* ---------- generate ---------- */}
            <Dialog open={open} onClose={() => setOpen(false)} maxWidth="xs" fullWidth slotProps={{ paper: dialogPaper }}>
                <DialogTitle sx={{ fontWeight: 700 }}>Generate coupon</DialogTitle>
                <DialogContent>
                    <CouponFields v={form} onChange={(k, val) => setForm((f) => ({ ...f, [k]: val }))} />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5 }}>
                    <Button onClick={() => setOpen(false)} sx={{ color: "rgba(255,255,255,.65)" }}>Cancel</Button>
                    <Button variant="contained" disabled={!canCreate || busy} onClick={create}
                        sx={{ borderRadius: 999, px: 3 }}>Create</Button>
                </DialogActions>
            </Dialog>

            {/* ---------- edit ---------- */}
            <Dialog open={Boolean(editing)} onClose={() => setEditing(null)} maxWidth="xs" fullWidth
                slotProps={{ paper: dialogPaper }}>
                <DialogTitle sx={{ fontWeight: 700 }}>Edit {editing?.row?.code}</DialogTitle>
                <DialogContent>
                    {/* Said before the fields, because it decides how carefully to read
                        them: once a code is out there, what can be changed narrows. */}
                    <Typography sx={{ color: "rgba(255,255,255,.55)", fontSize: 13.5 }}>
                        Used {editing?.row?.usedCount} time{editing?.row?.usedCount === 1 ? "" : "s"}.
                        Changes apply to new subscriptions only.
                    </Typography>

                    <CouponFields v={editing || {}} codeDisabled={editing?.row?.usedCount > 0}
                        onChange={(k, val) => setEditing((c) => ({ ...c, [k]: val }))} />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5 }}>
                    <Button onClick={() => setEditing(null)} sx={{ color: "rgba(255,255,255,.65)" }}>Cancel</Button>
                    <Button variant="contained" onClick={saveEdit}
                        disabled={busy || !editing?.code?.trim()
                            || !(Number(editing?.percent) >= 1 && Number(editing?.percent) <= 100)}
                        sx={{ borderRadius: 999, px: 3 }}>Save</Button>
                </DialogActions>
            </Dialog>

            {/* ---------- delete ---------- */}
            <Dialog open={Boolean(doomed)} onClose={() => setDoomed(null)} maxWidth="xs" fullWidth
                slotProps={{ paper: dialogPaper }}>
                <DialogTitle sx={{ fontWeight: 700 }}>Delete {doomed?.code}?</DialogTitle>
                <DialogContent>
                    <Typography sx={{ color: "rgba(255,255,255,.7)", fontSize: 14.5 }}>
                        It has never been used, so nothing depends on it. Anyone who has been given
                        this code will find it no longer works.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5 }}>
                    <Button onClick={() => setDoomed(null)} sx={{ color: "rgba(255,255,255,.65)" }}>Cancel</Button>
                    <Button variant="contained" color="error" disabled={busy} onClick={remove}
                        sx={{ borderRadius: 999, px: 3 }}>Delete</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
